import * as Tooltip from '@radix-ui/react-tooltip';
import type { ReactNode } from 'react';
import { explain } from '@/lib/glossary';
import { cn } from '@/lib/cn';

/**
 * The glossary tooltip. Wrapping a label in <Explain term="..."> makes it
 * self-documenting for operator/aggregator support who don't know our internals.
 *
 * Rendered as a dotted underline rather than an icon: it keeps dense tables
 * readable while signalling "there's an explanation here" on every term at once.
 */
export function Explain({
  term,
  children,
  className,
  side = 'top',
}: {
  term: string;
  children?: ReactNode;
  className?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
}) {
  const entry = explain(term);
  const label = children ?? entry?.title ?? term;

  if (!entry) return <>{label}</>;

  return (
    <Tooltip.Root delayDuration={200}>
      <Tooltip.Trigger asChild>
        <span
          tabIndex={0}
          className={cn(
            'cursor-help underline decoration-dotted decoration-text-faint underline-offset-[3px]',
            'hover:decoration-accent focus-visible:decoration-accent',
            className,
          )}
        >
          {label}
        </span>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side={side}
          sideOffset={6}
          collisionPadding={12}
          className={cn(
            'z-50 max-w-[300px] rounded border border-hairline-strong bg-surface-raised',
            'px-3 py-2 shadow-lg shadow-black/40 rise',
          )}
        >
          <div className="eyebrow mb-1 text-accent">{entry.title}</div>
          <p className="text-[12px] leading-[1.55] text-text">{entry.body}</p>
          {entry.hint && (
            <p className="mt-1.5 border-t border-hairline pt-1.5 text-[11.5px] leading-[1.5] text-text-dim">
              {entry.hint}
            </p>
          )}
          <Tooltip.Arrow className="fill-hairline-strong" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

/** Plain tooltip for one-off copy that doesn't belong in the glossary. */
export function Hint({
  content,
  children,
  side = 'top',
}: {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
}) {
  return (
    <Tooltip.Root delayDuration={200}>
      <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side={side}
          sideOffset={6}
          collisionPadding={12}
          className={cn(
            'z-50 max-w-[320px] rounded border border-hairline-strong bg-surface-raised',
            'px-2.5 py-1.5 text-[12px] leading-[1.5] text-text shadow-lg shadow-black/40',
          )}
        >
          {content}
          <Tooltip.Arrow className="fill-hairline-strong" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}
